import React from 'react';
import type { RuleCardStatus } from '../../receptionist-settings/types';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';

export function SettingsPageHeader({
  eyebrow,
  title,
  italic,
  description,
}: {
  eyebrow: string;
  title: string;
  italic?: string;
  description?: string;
}) {
  return (
    <section className="set-fade-up set-d-0 mb-7">
      <div className="mb-2.5 flex items-center gap-2">
        <Icon kind="sparkle" size={13} stroke={2} style={{ color: 'var(--set-green)' }} />
        <span
          className="text-[11px] font-bold uppercase tracking-[0.14em]"
          style={{ color: 'var(--set-green)' }}
        >
          {eyebrow}
        </span>
      </div>
      <h1 className="m-0 font-[var(--font-display)] text-[46px] font-semibold leading-none tracking-[-0.035em] text-[var(--ink-0)]">
        {title}
        {italic ? (
          <>
            {' '}
            <span className="set-count-gradient">{italic}</span>
          </>
        ) : null}
      </h1>
      {description ? (
        <p className="mt-3 max-w-[700px] text-[14px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-2)]">
          {description}
        </p>
      ) : null}
    </section>
  );
}

export function SettingsSectionHeader({
  num,
  title,
  italic,
  sub,
  railColor = 'var(--set-green)',
  action,
  actionType = 'link',
}: {
  num?: string;
  title: string;
  italic?: string;
  sub?: string;
  railColor?: string;
  action?: { label: string; onClick?: () => void };
  actionType?: 'link' | 'pill';
}) {
  return (
    <header
      className="flex items-center justify-between gap-4 border-b border-[var(--cream-2)] px-6 py-5"
      style={{
        background: 'var(--surface-mute)',
        borderLeft: `3px solid ${railColor}`,
        boxShadow: 'inset 0 -8px 12px -8px rgba(28, 24, 18, 0.06)',
      }}
    >
      <div className="flex min-w-0 items-start gap-3.5">
        {num ? (
          <span className="mt-0.5 inline-flex shrink-0 items-center justify-center rounded-[5px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2 py-0.5 font-[var(--font-mono)] text-[11px] font-semibold text-[var(--ink-2)] shadow-[var(--sh-1)]">
            {num}
          </span>
        ) : null}
        <div className="min-w-0">
          <div className="mb-0.5 font-[var(--font-display)] text-[18px] font-semibold leading-tight tracking-[-0.02em] text-[var(--ink-0)]">
            {title}
            {italic ? (
              <>
                {' '}
                <span className="set-serif text-[22px] font-normal tracking-[-0.01em]">{italic}</span>
              </>
            ) : null}
          </div>
          {sub ? (
            <div className="max-w-[70ch] text-[13px] leading-[1.5] tracking-[-0.005em] text-[var(--ink-2)]">
              {sub}
            </div>
          ) : null}
        </div>
      </div>
      {action && actionType === 'link' ? (
        <button
          type="button"
          onClick={action.onClick}
          className="inline-flex shrink-0 items-center gap-1.5 border-0 bg-transparent p-0 text-[13px] font-semibold"
          style={{ color: 'var(--set-green-deep)' }}
        >
          {action.label}
          <Icon kind="right" size={12} stroke={2} />
        </button>
      ) : null}
      {action && actionType === 'pill' ? (
        <button
          type="button"
          onClick={action.onClick}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-[999px] border px-[13px] py-1.5 text-[12px] font-semibold tracking-[-0.005em]"
          style={{
            background: 'var(--set-brass-tint)',
            borderColor: 'rgba(168, 132, 92, 0.18)',
            color: 'var(--set-brass-deep)',
          }}
        >
          <Icon kind="sparkle" size={11} stroke={2} />
          {action.label}
        </button>
      ) : null}
    </header>
  );
}

export function SettingsFieldRow({
  title,
  description,
  isDirty,
  children,
  last,
}: {
  title: string;
  description?: string;
  isDirty?: boolean;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={[
        'relative grid grid-cols-[minmax(0,1fr)_auto] items-start gap-7 px-6 py-[18px]',
        last ? '' : 'border-b border-dashed border-[var(--cream-2)]',
        isDirty ? 'set-dirty-wash' : '',
      ].join(' ')}
    >
      {isDirty ? <span className="set-dirty-rail" /> : null}
      <div>
        <div className="mb-1 text-[13.5px] font-semibold leading-snug tracking-[-0.005em] text-[var(--ink-0)]">
          {title}
        </div>
        {description ? (
          <div className="max-w-[62ch] text-[12.5px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-2)]">
            {description}
          </div>
        ) : null}
      </div>
      <div className="flex min-w-0 justify-end">{children}</div>
    </div>
  );
}

export function SettingsToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div
      role="switch"
      aria-checked={value}
      className="set-toggle"
      data-on={value}
      onClick={() => onChange(!value)}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onChange(!value);
        }
      }}
      tabIndex={0}
    />
  );
}

export function SettingsSelect({
  value,
  onChange,
  options,
  width = 180,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  width?: number;
}) {
  return (
    <div className="relative" style={{ width }}>
      <select
        className="set-select cursor-pointer appearance-none pr-8 shadow-[var(--sh-1)]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <Icon
        kind="down"
        size={14}
        stroke={2}
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-2)]"
      />
    </div>
  );
}

export function SettingsSegmented({
  options,
  value,
  onChange,
}: {
  options: { id: string; label: string; subtitle?: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex gap-px rounded-[10px] border border-[var(--cream-2)] bg-[var(--surface-mute)] p-0.5">
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={[
              'min-w-[140px] cursor-pointer rounded-lg border-0 px-4 py-2.5 text-center transition-all duration-200',
              active ? 'bg-[var(--cream-1)] shadow-[var(--sh-1)]' : 'bg-transparent',
            ].join(' ')}
          >
            <div
              className={[
                'text-[13px] tracking-[-0.005em]',
                active ? 'font-semibold text-[var(--ink-0)]' : 'font-medium text-[var(--ink-1)]',
              ].join(' ')}
            >
              {opt.label}
            </div>
            {opt.subtitle ? (
              <div
                className="mt-0.5 font-[var(--font-mono)] text-[10.5px]"
                style={{ color: active ? 'var(--set-brass-deep)' : 'var(--ink-3)' }}
              >
                {opt.subtitle}
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function SettingsSlider({
  value,
  onChange,
  min = 0,
  max = 100,
  leftLabel,
  rightLabel,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  leftLabel: string;
  rightLabel: string;
}) {
  return (
    <div className="w-[280px]">
      <input
        type="range"
        className="set-slider"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
      />
      <div className="mt-1 flex justify-between text-[11px] font-medium text-[var(--ink-2)]">
        <span>{leftLabel}</span>
        <span className="font-[var(--font-mono)] font-semibold text-[var(--ink-1)]">{value}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );
}

const RULE_STATUS: Record<
  RuleCardStatus,
  { border: string; bg: string; color: string; label: string; icon: IconKind }
> = {
  enabled: {
    border: 'var(--set-green)',
    bg: 'var(--set-green-soft)',
    color: 'var(--set-green-deep)',
    label: 'Enabled',
    icon: 'check-circ',
  },
  'needs-config': {
    border: 'var(--set-warning)',
    bg: 'var(--set-warning-soft)',
    color: 'var(--set-warning)',
    label: 'Needs config',
    icon: 'alert',
  },
  rejected: {
    border: 'var(--set-negative)',
    bg: 'var(--set-negative-soft)',
    color: 'var(--set-negative)',
    label: 'Rejected',
    icon: 'x',
  },
};

export function SettingsRuleCard({
  status,
  quote,
  explanation,
  action,
}: {
  status: RuleCardStatus;
  quote: string;
  explanation: string;
  action?: string;
}) {
  const cfg = RULE_STATUS[status];
  return (
    <div
      className="flex flex-col gap-3 rounded-r-xl border border-[var(--cream-2)] px-[22px] py-[18px] shadow-[var(--sh-1)]"
      style={{ borderLeft: `3px solid ${cfg.border}`, background: 'var(--cream-1)' }}
    >
      <div className="flex items-start justify-between gap-4">
        <p className="m-0 flex-1 text-[14.5px] font-medium leading-[1.5] tracking-[-0.005em] text-[var(--ink-0)]">
          &ldquo;{quote}&rdquo;
        </p>
        <span
          className="inline-flex shrink-0 items-center gap-1.5 rounded-[999px] px-2.5 py-0.5 text-[11px] font-semibold tracking-[-0.005em]"
          style={{ background: cfg.bg, color: cfg.color }}
        >
          <Icon kind={cfg.icon} size={11} stroke={2} />
          {cfg.label}
        </span>
      </div>
      <div
        className="flex items-start gap-2 rounded-lg border px-3.5 py-3"
        style={{ background: 'var(--set-green-wash)', borderColor: 'var(--set-green-soft)' }}
      >
        <Icon kind="sparkle" size={12} stroke={2} className="mt-0.5 shrink-0" style={{ color: 'var(--set-green)' }} />
        <p className="m-0 text-[13px] font-medium leading-[1.55] tracking-[-0.005em] text-[var(--ink-1)]">
          {explanation}
        </p>
      </div>
      {action ? (
        <div className="text-right text-[12px] italic text-[var(--ink-2)]">{action}</div>
      ) : null}
    </div>
  );
}

export function SettingsMiniStat({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string | number;
  valueColor?: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-3.5 pb-4 shadow-[var(--sh-1)]">
      <div className="set-eyebrow mb-1.5 text-[10px]">{label}</div>
      <span
        className="font-[var(--font-display)] text-[38px] font-semibold leading-none tracking-[-0.03em] tabular-nums"
        style={{ color: valueColor ?? 'var(--ink-0)' }}
      >
        {value}
      </span>
    </div>
  );
}

export function SettingsDirtyBlock({
  isDirty,
  children,
  className = '',
}: {
  isDirty?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={['relative', isDirty ? 'set-dirty-wash' : '', className].join(' ')}>
      {isDirty ? <span className="set-dirty-rail" /> : null}
      {children}
    </div>
  );
}
